// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Box, Button, Divider, Group, Stack, Text, Title } from '@mantine/core';
import { IconCheck, IconEdit, IconFileDescription, IconPackgeExport, IconTrash } from '@tabler/icons';
import { FC, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { Planning, PlanningGroup } from '../../Types/ResourceT';
import ResourcePlanningS from '../../Service/restapi/resourcePlanningS';
import ResourcePlanningTable from '../../Organismns/Resources/table/planning/ResourcePlanningTable';
import BookitupAddIcon from '../../Atoms/BookitupAddIcon';
import { WindowContext } from '../../Service/Context/WindowContext';
import { EventResourcePlanningContext } from '../../Service/Context/EventResourcePlanningContext';
import PanePaper from '../../Molecules/PanePaper';
import { EventContext } from '../../Service/Context/EventContext';
import {
  openCreateDeliverySlipModal,
  openCreatePackingListModal,
  openPlanningGroupModal,
  openDeletePlanningGroupModal,
} from './EventPlanningsModals';
import { openAddResourcePlanningModal } from './AddEventResourcePlanningModal';
import GrayBox from '../../Atoms/GrayBox';

interface Props {
  group: PlanningGroup;
}

const hasUnconfirmedPlanning = (plannings: Planning[]): boolean =>
  plannings.some((planning) => !planning.warehousePlanning || hasUnconfirmedPlanning(planning.children ?? []));

const formatPlanningRange = (begin: Date, end: Date) => {
  const from = moment(begin);
  const to = moment(end);
  const sameDay = from.isSame(to, 'day');
  const startsAtMidnight = from.format('HH:mm') === '00:00';
  const endsAtEndOfDay = to.format('HH:mm') === '23:59';
  const isAllDay = sameDay && startsAtMidnight && endsAtEndOfDay;
  const isMultiDayAllDay = !sameDay && startsAtMidnight && endsAtEndOfDay;
  const fromDate = from.format('DD.MM.YYYY');
  const toDate = to.format('DD.MM.YYYY');
  const fromTime = from.format('HH:mm');
  const toTime = to.format('HH:mm');

  if (isAllDay) {
    return `Am ${fromDate}, ganztägig`;
  }

  if (sameDay) {
    return `Am ${fromDate} von ${fromTime} bis ${toTime} Uhr`;
  }

  if (isMultiDayAllDay) {
    return `Vom ${fromDate} bis ${toDate}, ganztägig`;
  }

  return `Vom ${fromDate}, ${fromTime} Uhr bis ${toDate}, ${toTime} Uhr`;
};

const EventPlanningGroup: FC<Props> = ({ group }) => {
  const { id, description, begin, end } = group;
  const [loading, setLoading] = useState(false);
  const [plannings, setPlannings] = useState<Planning[]>([]);
  const { sm } = useContext(WindowContext);
  const { editGroup, deleteGroup } = useContext(EventResourcePlanningContext);
  const { event } = useContext<any>(EventContext);

  const fetchPlannings = () => {
    setLoading(true);
    ResourcePlanningS.fetchPlannings(id)
      .then(setPlannings)
      .finally(() => setLoading(false));
  };

  const onDelete = async (groupId: string) => {
    const deleted = await ResourcePlanningS.deletePlanningGroup(groupId);
    if (deleted) {
      deleteGroup(groupId);
    }
  };

  useEffect(() => {
    fetchPlannings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onConfirmAll = async () => {
    const confirmed = await ResourcePlanningS.confirmResourcePlannings(id);
    if (confirmed) {
      fetchPlannings();
    }
  };

  const hasUnconfirmedPlannings = hasUnconfirmedPlanning(plannings);
  const hasPlannings = plannings.length > 0;
  const addResource = () => openAddResourcePlanningModal({ group, refresh: fetchPlannings });
  const actions = <BookitupAddIcon onClick={addResource} />;
  const actionButtonSx = (theme: any) => ({
    backgroundColor: theme.colorScheme === 'dark' ? '#1D5191' : '#DFEBFB',
    color: theme.colorScheme === 'dark' ? '#DFEBFB' : '#1D5191',
    border: 'none',
  });

  return (
    <PanePaper>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: sm ? 'stretch' : 'flex-start',
          gap: 12,
          flexDirection: sm ? 'column' : 'row',
        }}
      >
        <Stack spacing={4} sx={{ flex: 1 }}>
          <Group position="apart" align="flex-start" noWrap={!sm}>
            <Group spacing={6} noWrap>
              <Text size="lg" weight={600}>
                {description}
              </Text>
              <ActionIcon
                size="sm"
                variant="subtle"
                color="gray"
                onClick={() => openPlanningGroupModal({ initial: group, onFinish: editGroup, dates: [] })}
              >
                <IconEdit size={16} />
              </ActionIcon>
            </Group>
            {sm && actions}
          </Group>
          <Text color="dimmed" size="xs" weight="lighter">
            {formatPlanningRange(begin, end)}
          </Text>
          <Group my="sm">
            {hasUnconfirmedPlannings && (
              <Button
                variant="outline"
                leftIcon={<IconCheck size={14} />}
                size="xs"
                onClick={onConfirmAll}
                sx={actionButtonSx}
              >
                Alle Planungen bestätigen
              </Button>
            )}
            {hasPlannings && (
              <Button
                size="xs"
                variant="outline"
                leftIcon={<IconFileDescription size={16} />}
                onClick={() =>
                  openCreateDeliverySlipModal({
                    planningGroupId: id,
                    customerId: event?.customerId,
                    eventId: event?.id,
                  })
                }
                sx={actionButtonSx}
              >
                Dokument erstellen
              </Button>
            )}
          </Group>
        </Stack>
        {!sm && actions}
      </Box>
      <Title order={5} ml="md" my="md">
        Plannings
      </Title>
      <Box>{!loading && <ResourcePlanningTable plannings={plannings} refresh={fetchPlannings} />}</Box>
      <Group position="center" mt="xl">
        <Button
          size="xs"
          variant="subtle"
          color="red"
          leftIcon={<IconTrash size={16} />}
          onClick={() => openDeletePlanningGroupModal({ group, onDelete: () => onDelete(group.id) })}
        >
          Löschen
        </Button>
      </Group>
    </PanePaper>
  );
};

export default EventPlanningGroup;
